/**
 * Save the Date — Event Customization Fields
 *
 * Handles:
 * - "Custom..." occasion dropdown toggle (reveals inline text input)
 * - Date validation (day-vs-month, year range)
 * - Real-time form validity tracking
 * - Exposes window.SaveTheDateFields API:
 *     isValid()  → boolean
 *     getData()  → { occasion, names, eventDate, location, summary }
 *
 * Coordinates with photo-upload.js: both must pass for Add to Cart to enable.
 */
(function () {
  'use strict';

  // ── DOM references ──
  var container = document.querySelector('[data-std-fields]');
  if (!container) return;

  var occasionSelect   = container.querySelector('[data-std-occasion]');
  var customWrap       = container.querySelector('[data-std-custom-occasion-wrap]');
  var customInput      = container.querySelector('[data-std-custom-occasion]');
  var namesInput       = container.querySelector('[data-std-names]');
  var monthSelect      = container.querySelector('[data-std-month]');
  var dayInput         = container.querySelector('[data-std-day]');
  var yearInput        = container.querySelector('[data-std-year]');
  var locationInput    = container.querySelector('[data-std-location]');
  var dateErrorEl      = container.querySelector('[data-std-date-error]');

  // ── Days-per-month lookup ──
  var MONTH_DAYS = {
    January: 31, February: 29, March: 31, April: 30,
    May: 31, June: 30, July: 31, August: 31,
    September: 30, October: 31, November: 30, December: 31
  };

  // =========================================================================
  // Custom Occasion Toggle
  // =========================================================================

  function handleOccasionChange() {
    var isCustom = occasionSelect.value === '__custom__';

    if (isCustom) {
      customWrap.removeAttribute('hidden');
      // Trigger the CSS animation by adding the visible class on next frame
      requestAnimationFrame(function () {
        customWrap.classList.add('std-fields__custom-occasion--visible');
      });
      customInput.focus();
    } else {
      customWrap.classList.remove('std-fields__custom-occasion--visible');
      // Wait for transition to finish, then hide
      setTimeout(function () {
        if (occasionSelect.value !== '__custom__') {
          customWrap.setAttribute('hidden', '');
        }
      }, 260);
      customInput.value = '';
    }

    updateValidity();
  }

  // =========================================================================
  // Date Validation
  // =========================================================================

  /**
   * Validate the 3-part date fields.
   * Returns { valid: boolean, error: string|null }
   */
  function validateDate() {
    var month = monthSelect.value;
    var day   = dayInput.value.trim();
    var year  = yearInput.value.trim();

    // All empty → not yet filled (no error message, just invalid)
    if (!month && !day && !year) {
      return { valid: false, error: null };
    }

    // Partially filled
    if (!month || !day || !year) {
      return { valid: false, error: null };
    }

    // Day: must be numeric
    var dayNum = parseInt(day, 10);
    if (isNaN(dayNum) || dayNum < 1) {
      return { valid: false, error: 'Please enter a valid day.' };
    }

    // Day: check against month
    var maxDay = MONTH_DAYS[month] || 31;
    if (dayNum > maxDay) {
      return { valid: false, error: month + ' only has ' + maxDay + ' days.' };
    }

    // Year: must be 4 digits and in range
    var yearNum = parseInt(year, 10);
    if (isNaN(yearNum) || year.length !== 4) {
      return { valid: false, error: 'Please enter a 4-digit year.' };
    }
    if (yearNum < 2025 || yearNum > 2035) {
      return { valid: false, error: 'Year must be between 2025 and 2035.' };
    }

    // February leap year check
    if (month === 'February' && dayNum === 29) {
      var isLeap = (yearNum % 4 === 0 && yearNum % 100 !== 0) || (yearNum % 400 === 0);
      if (!isLeap) {
        return { valid: false, error: yearNum + ' is not a leap year. February has 28 days.' };
      }
    }

    return { valid: true, error: null };
  }

  /**
   * Show or hide date error message.
   */
  function updateDateError() {
    var result = validateDate();
    if (dateErrorEl) {
      if (result.error) {
        dateErrorEl.textContent = result.error;
        dateErrorEl.removeAttribute('hidden');
      } else {
        dateErrorEl.setAttribute('hidden', '');
        dateErrorEl.textContent = '';
      }
    }
  }

  // =========================================================================
  // Day input: strip non-numeric characters
  // =========================================================================

  function handleDayInput() {
    dayInput.value = dayInput.value.replace(/[^0-9]/g, '');
  }

  function handleYearInput() {
    yearInput.value = yearInput.value.replace(/[^0-9]/g, '');
  }

  // =========================================================================
  // Overall Validity
  // =========================================================================

  /**
   * Check if all required fields are valid.
   */
  function isValid() {
    // Occasion
    var occasionVal = occasionSelect.value;
    if (!occasionVal) return false;
    if (occasionVal === '__custom__') {
      if (!customInput.value.trim()) return false;
    }

    // Names
    if (!namesInput.value.trim()) return false;

    // Date
    var dateResult = validateDate();
    if (!dateResult.valid) return false;

    return true;
  }

  /**
   * Get all field data for cart submission.
   * Returns { occasion, names, eventDate, location, summary }
   */
  function getData() {
    var occasion = occasionSelect.value === '__custom__'
      ? customInput.value.trim()
      : occasionSelect.value;

    var names = namesInput.value.trim();

    // Compose date string: "Month DD, YYYY"
    var month = monthSelect.value;
    var day   = dayInput.value.trim();
    var year  = yearInput.value.trim();
    var eventDate = month && day && year
      ? month + ' ' + parseInt(day, 10) + ', ' + year
      : '';

    var location = locationInput.value.trim();

    // Build visible summary: "Names · Date · Occasion" (+ optional Location)
    var parts = [];
    if (names) parts.push(names);
    if (eventDate) parts.push(eventDate);
    if (occasion) parts.push(occasion);
    if (location) parts.push(location);
    var summary = parts.join(' \u00B7 ');

    return {
      occasion: occasion,
      names: names,
      eventDate: eventDate,
      location: location,
      summary: summary
    };
  }

  // =========================================================================
  // Submit Button Coordination
  // =========================================================================

  /**
   * Notify photo-upload.js (or any listener) that validity has changed.
   * This triggers a re-check of the Add to Cart button state.
   */
  function updateValidity() {
    updateDateError();

    // If PhotoUpload exists, ask it to re-evaluate the submit button.
    // PhotoUpload.updateSubmitButton checks SaveTheDateFields.isValid() internally.
    if (window.PhotoUpload && typeof window.PhotoUpload.refreshSubmitButton === 'function') {
      window.PhotoUpload.refreshSubmitButton();
    }
  }

  // =========================================================================
  // Event Listeners
  // =========================================================================

  function init() {
    if (occasionSelect) {
      occasionSelect.addEventListener('change', handleOccasionChange);
    }

    if (customInput) {
      customInput.addEventListener('input', updateValidity);
    }

    if (namesInput) {
      namesInput.addEventListener('input', updateValidity);
    }

    if (monthSelect) {
      monthSelect.addEventListener('change', function () {
        updateValidity();
      });
    }

    if (dayInput) {
      dayInput.addEventListener('input', function () {
        handleDayInput();
        updateValidity();
      });
    }

    if (yearInput) {
      yearInput.addEventListener('input', function () {
        handleYearInput();
        updateValidity();
      });
    }

    if (locationInput) {
      locationInput.addEventListener('input', updateValidity);
    }

    // Initial state
    updateValidity();
  }

  // ── Initialize ──
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ── Public API ──
  window.SaveTheDateFields = {
    isValid: isValid,
    getData: getData
  };
})();
